/**
 * /app/platform — platform super-admin god-view (multi-tenancy Phase F,
 * Slice 5 + lifecycle controls Phase 1). Cross-org + cross-user tables for
 * the platform operator (arjunbindusunil@gmail.com). Double-gated: the nav
 * link only shows for platform admins, this component redirects non-admins,
 * and the backend (RequirePlatformAdmin on /v1/admin/*) is the real
 * boundary.
 *
 * Lifecycle controls (Phase 1): per-org disable/enable + a per-org "Digital
 * twin" toggle (the foundry pack), and per-user disable/enable. Each writes
 * a Postgres-authoritative flag enforced fail-closed at orgctx + AuthMe.
 * Disable is destructive-ish (locks members out), so it uses a two-click
 * inline confirm; the twin toggle is freely reversible.
 *
 * Layout (2026-07): this is an internal god-view with several dense tables,
 * so it's organised into tabs (Organisations · Users · Gateways · Mock-device factory ·
 * Firmware/OTA · Knowledge candidates · Acquisition learning) instead of one long scroll; the tab is in the URL
 * (?tab=), so a link opens it. The Organisations table is a real
 * columnar table — the toggles live in labelled columns and the rare/
 * destructive actions collapse into a per-row ⋯ menu, so an org name is never
 * squeezed to a couple of characters by a wall of buttons.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  Activity,
  Atom,
  Ban,
  Boxes,
  Brain,
  Building2,
  Check,
  ChevronRight,
  CircleCheck,
  Copy,
  Eraser,
  Factory,
  FileCog,
  FlaskConical,
  FolderKanban,
  GitBranch,
  GraduationCap,
  Lightbulb,
  ListChecks,
  Loader2,
  MoreHorizontal,
  Network,
  Rocket,
  Router,
  Search,
  Shield,
  Trash2,
  UserCheck,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import {
  factoryApi,
  platformAdminApi,
  backendErrorMessage,
  type OrgSummary,
  type UserSummary,
  type UserOrgMembership,
} from "@/lib/api";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { useToast } from "@/lib/ui/toast";
import { DangerConfirmDialog } from "@/lib/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/lib/ui/dropdown-menu";
import { cn } from "@/lib/ui/cn";
import { FactoryPanel } from "./factory-panel";
import { FirmwareConsole } from "./firmware-console";
import { GatewaysConsole } from "./gateways-console";
import { KnowledgeCandidatesPanel } from "./knowledge-candidates-panel";
import { AcquisitionLearningPanel } from "./acquisition-learning-panel";
import { OrgToggle } from "./org-toggle";
import { ConsultantLimitsCell, ConsultantLimitsSheet } from "./copilot-limits-panel";
import { ProductsCell, ProductsSheet } from "./suite-products-panel";
import { CopySetupSheet } from "./copy-setup-panel";
import { useQueryClient } from "@tanstack/react-query";
import { knowledgeShownToast } from "@/components/twin/knowledge/plant-sees-words";
import { KNOWLEDGE_CUSTOMER_VIEW_KEY } from "@/lib/api/twin-knowledge";
import {
  PROJECTS_GATE_FAILED,
  PROJECTS_GATE_HEADER_TITLE,
  PROJECTS_GATE_OFF_TITLE,
  PROJECTS_GATE_ON_TITLE,
  projectsGateOpen,
  projectsGateToast,
} from "./improvement-projects-gate";

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

// Maps an org's data_class to its Real/Sim badge — green "Real", amber "Sim",
// neutral "Mixed"/"Empty" — so the operator can tell dummy demo orgs from real
// tenants at a glance.
function dataClassBadge(dc: OrgSummary["data_class"]): {
  variant: "success" | "warning" | "secondary";
  label: string;
} {
  switch (dc) {
    case "real":
      return { variant: "success", label: "Real" };
    case "simulated":
      return { variant: "warning", label: "Sim" };
    case "mixed":
      return { variant: "secondary", label: "Mixed" };
    default:
      return { variant: "secondary", label: "Empty" };
  }
}

const PLATFORM_TABS = ["orgs", "users", "gateways", "factory", "firmware", "knowledge", "acquisition"] as const;
type PlatformTab = (typeof PLATFORM_TABS)[number];

/** The tab a URL names (?tab=), else Organisations. */
function platformTabOf(v: string | null): PlatformTab {
  return (PLATFORM_TABS as readonly string[]).includes(v ?? "") ? (v as PlatformTab) : "orgs";
}

export function PlatformConsole() {
  const { identityLoaded } = useAuth();
  const { isPlatformAdmin } = useOrg();
  const toast = useToast();
  const qc = useQueryClient();

  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Which section tab is showing, in the URL (?tab=) so a link opens it; the Gateways section keeps its filters
  // there too. Orgs first — it's the primary god-view. A new tab starts from a clean URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = platformTabOf(searchParams.get("tab"));
  const setActiveTab = (t: PlatformTab) => setSearchParams(t === "orgs" ? {} : { tab: t });
  // Two-click inline confirm: holds the id pending a confirm click.
  const [confirmOrg, setConfirmOrg] = useState<string | null>(null);
  const [confirmUser, setConfirmUser] = useState<string | null>(null);
  // Reject is destructive (denies access), so it gets the same two-click
  // inline confirm; approve is a single click.
  const [confirmReject, setConfirmReject] = useState<string | null>(null);
  // Org pending a hard delete — drives the typed-confirmation dialog. Delete
  // is irreversible (cascades all the org's data), so unlike disable it gets a
  // type-the-name gate, not a two-click inline confirm.
  const [deleteOrgTarget, setDeleteOrgTarget] = useState<OrgSummary | null>(null);
  const [purgeSimTarget, setPurgeSimTarget] = useState<OrgSummary | null>(null);
  // The org whose AI Casting Consultant limits sheet is open (migration 0201): its limits, usage and resets.
  const [limitsOrg, setLimitsOrg] = useState<OrgSummary | null>(null);
  // The org whose suite products are open (migration 0227), or none.
  const [productsOrg, setProductsOrg] = useState<OrgSummary | null>(null);
  // The org that is to take a copy of another org's setup (the "Copy setup" sheet, which lists every other org to copy from).
  const [copySetupOrg, setCopySetupOrg] = useState<OrgSummary | null>(null);
  // Per-row in-flight guard so a double-click can't fire two writes.
  const [busy, setBusy] = useState<Set<string>>(new Set());

  // List controls (#579): client-side search + real/sim filter + pagination.
  // The lists already load in full (platformAdminApi.orgs/users page everything),
  // so narrowing + paging is a pure render concern — no extra fetches.
  const PAGE_SIZE = 10;
  const [orgSearch, setOrgSearch] = useState("");
  const [orgDataClass, setOrgDataClass] = useState<
    "all" | "real" | "simulated"
  >("all");
  const [orgPage, setOrgPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);

  // User → org-memberships expander (#580): which user row is open, plus a
  // lazy-loaded cache of each user's memberships (fetched once on first expand)
  // and a per-user error flag.
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userOrgs, setUserOrgs] = useState<
    Record<string, UserOrgMembership[]>
  >({});
  const [userOrgsErr, setUserOrgsErr] = useState<Record<string, boolean>>({});

  const setRowBusy = (id: string, on: boolean) =>
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  // ── Derived list slices (#579) ────────────────────────────────────────
  // Companies: filter by real/sim (reusing the data_class marker) + a
  // name/slug search, then page at PAGE_SIZE. The page is clamped in render
  // (effOrgPage) so a filter that shrinks the list can't strand us on an empty
  // page; search/filter onChange also resets to page 1.
  const filteredOrgs = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    return orgs.filter((o) => {
      if (orgDataClass === "real" && o.data_class !== "real") return false;
      if (
        orgDataClass === "simulated" &&
        o.data_class !== "simulated" &&
        o.data_class !== "mixed"
      )
        return false;
      if (!q) return true;
      return (
        o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q)
      );
    });
  }, [orgs, orgSearch, orgDataClass]);
  const orgPageCount = Math.max(1, Math.ceil(filteredOrgs.length / PAGE_SIZE));
  const effOrgPage = Math.min(orgPage, orgPageCount);
  const pagedOrgs = filteredOrgs.slice(
    (effOrgPage - 1) * PAGE_SIZE,
    effOrgPage * PAGE_SIZE,
  );

  // Users: name/email search, then page.
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.display_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [users, userSearch]);
  const userPageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const effUserPage = Math.min(userPage, userPageCount);
  const pagedUsers = filteredUsers.slice(
    (effUserPage - 1) * PAGE_SIZE,
    effUserPage * PAGE_SIZE,
  );

  // Access requests (sign-up approval gate) — users who submitted the
  // registration form and are awaiting a decision. Surfaced at the top of the
  // Users tab (the actionable queue) and counted on the Users tab badge.
  const pending = useMemo(
    () =>
      users.filter(
        (u) => u.approval_status === "pending" && u.approval_submitted,
      ),
    [users],
  );

  // Expand/collapse a user row, lazy-loading their org memberships on first
  // open (#580). Cached in userOrgs so re-expanding is instant; a fetch error
  // is remembered per-user so the panel can offer nothing-loaded feedback.
  const toggleUserExpand = (userId: string) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }
    setExpandedUser(userId);
    if (userOrgs[userId] === undefined) {
      setRowBusy(userId + ":orgs", true);
      setUserOrgsErr((p) => ({ ...p, [userId]: false }));
      platformAdminApi
        .userOrgs(userId)
        .then((orgs) => setUserOrgs((p) => ({ ...p, [userId]: orgs })))
        .catch(() => setUserOrgsErr((p) => ({ ...p, [userId]: true })))
        .finally(() => setRowBusy(userId + ":orgs", false));
    }
  };

  useEffect(() => {
    if (!isPlatformAdmin) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([platformAdminApi.orgs(), platformAdminApi.users()])
      .then(([o, u]) => {
        if (cancelled) return;
        setOrgs(o.items);
        setUsers(u.items);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load the platform console.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin]);

  // ── Mutations (optimistic + toast) ────────────────────────────────────
  const toggleOrgDisabled = (o: OrgSummary) => {
    const next = !o.is_disabled;
    setConfirmOrg(null);
    setRowBusy(o.id, true);
    platformAdminApi
      .setOrgDisabled(o.id, next)
      .then(() => {
        setOrgs((prev) =>
          prev.map((x) => (x.id === o.id ? { ...x, is_disabled: next } : x)),
        );
        toast.push("ok", next ? `${o.name} disabled` : `${o.name} enabled`);
      })
      .catch(() => toast.push("error", "Couldn't update the organisation."))
      .finally(() => setRowBusy(o.id, false));
  };

  const toggleOrgTwin = (o: OrgSummary) => {
    const next = !o.twin_enabled;
    setRowBusy(o.id + ":twin", true);
    platformAdminApi
      .setOrgTwin(o.id, next)
      .then(() => {
        setOrgs((prev) =>
          prev.map((x) => (x.id === o.id ? { ...x, twin_enabled: next } : x)),
        );
        toast.push(
          "ok",
          next
            ? `Digital twin enabled for ${o.name}`
            : `Digital twin disabled for ${o.name}`,
        );
      })
      .catch(() => toast.push("error", "Couldn't toggle the digital twin."))
      .finally(() => setRowBusy(o.id + ":twin", false));
  };

  // Switch the org's genealogy model (legacy ↔ new). Flips its latest
  // plant.source server-side — the SAME signal the new emitter + useNewModel
  // gate read — so the change takes effect on the org's next regenerate. A
  // 409 ("no_provisioned_plant") means the org hasn't provisioned a plant
  // yet, so there's nothing to switch.
  const toggleOrgGenealogyModel = (o: OrgSummary) => {
    const next = o.genealogy_model === "new" ? "legacy" : "newmodel";
    const nextLabel: "new" | "legacy" = next === "newmodel" ? "new" : "legacy";
    setRowBusy(o.id + ":model", true);
    platformAdminApi
      .setOrgGenealogyModel(o.id, next)
      .then(() => {
        setOrgs((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, genealogy_model: nextLabel } : x,
          ),
        );
        toast.push(
          "ok",
          nextLabel === "new"
            ? `Switched ${o.name} to the new genealogy model — Regenerate its heats to populate it.`
            : `Switched ${o.name} back to the legacy genealogy model.`,
        );
      })
      .catch((err: unknown) => {
        const code =
          typeof err === "object" && err && "body" in err
            ? (err as { body?: { error?: string } }).body?.error
            : undefined;
        toast.push(
          "error",
          code === "no_provisioned_plant"
            ? `${o.name} has no provisioned plant to switch — provision one first.`
            : "Couldn't switch the genealogy model.",
        );
      })
      .finally(() => setRowBusy(o.id + ":model", false));
  };

  // Per-org physics-twin opt-in (org_settings.physics_twin_enabled) — gates
  // the S2 live physics tick, the reconcile worker and /v1/twin/simcore/*.
  // Freely reversible, default OFF; pack behaviour graphs are inert without
  // it, so flipping it on is what makes an org's equipment simulate live.
  const toggleOrgPhysicsTwin = (o: OrgSummary) => {
    const next = !o.physics_twin_enabled;
    setRowBusy(o.id + ":physics", true);
    platformAdminApi
      .setOrgPhysicsTwin(o.id, next)
      .then(() => {
        setOrgs((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, physics_twin_enabled: next } : x,
          ),
        );
        toast.push(
          "ok",
          next
            ? `Physics twin enabled for ${o.name} — behaviour-graph equipment now simulates live.`
            : `Physics twin disabled for ${o.name}.`,
        );
      })
      .catch(() => toast.push("error", "Couldn't toggle the physics twin."))
      .finally(() => setRowBusy(o.id + ":physics", false));
  };

  // Per-org demo-simulator kill switch (org_settings.simulator_enabled,
  // migration 0092, #581). Opt-OUT: default ON, so this reads "on" for every
  // org until explicitly frozen. Freezing halts the simulator tick's new
  // readings for THIS org only — the per-org alternative to the global
  // SIMULATOR_DISABLED env flag, used e.g. just before importing a customer's
  // real data.
  const toggleOrgSimulator = (o: OrgSummary) => {
    const next = !o.simulator_enabled;
    setRowBusy(o.id + ":sim", true);
    platformAdminApi
      .setOrgSimulator(o.id, next)
      .then(() => {
        setOrgs((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, simulator_enabled: next } : x,
          ),
        );
        toast.push(
          "ok",
          next
            ? `Simulator resumed for ${o.name}.`
            : `Simulator frozen for ${o.name} — no new demo readings will be generated.`,
        );
      })
      .catch(() => toast.push("error", "Couldn't toggle the simulator."))
      .finally(() => setRowBusy(o.id + ":sim", false));
  };

  // Per-org analysis plan view (org_settings.analysis_plan_view, migration 0169,
  // plant-analytics S14): shows the Plan tab of the org's correlations page (the
  // analysis plan, its results matrix, what is not computable yet). Default OFF,
  // freely reversible; the org's members see the change on their next read of it.
  const toggleOrgPlanView = (o: OrgSummary) => {
    const next = !o.analysis_plan_view;
    setRowBusy(o.id + ":planview", true);
    platformAdminApi
      .setOrgAnalysisPlanView(o.id, next)
      .then(() => {
        setOrgs((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, analysis_plan_view: next } : x,
          ),
        );
        toast.push(
          "ok",
          next
            ? `Plan view shown for ${o.name}: the Plan tab is on its correlations page.`
            : `Plan view hidden for ${o.name}.`,
        );
      })
      .catch(() => toast.push("error", "Couldn't switch the plan view."))
      .finally(() => setRowBusy(o.id + ":planview", false));
  };

  // The knowledge store's customer switch (org_settings.knowledge_customer_visible,
  // migration 0156, plant-analytics B-6b): opens the Knowledge page to the org's
  // members (its confirmed findings marked for customers). Default OFF, freely
  // reversible; the members see the change on their next read of the findings.
  const toggleOrgKnowledge = (o: OrgSummary) => {
    const next = !o.knowledge_customer_visible;
    setRowBusy(o.id + ":knowledge", true);
    platformAdminApi
      .setOrgKnowledgeVisible(o.id, next)
      .then(() => {
        setOrgs((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, knowledge_customer_visible: next } : x,
          ),
        );
        void qc.invalidateQueries({ queryKey: KNOWLEDGE_CUSTOMER_VIEW_KEY }); // the Knowledge page's staff line
        if (!next) {
          toast.push("ok", `Knowledge hidden from ${o.name}.`);
          return;
        }
        // Say what the plant now sees, and warn when that is nothing (Arjun 2026-10-23: a plant admin met a page of
        // zeros): the counts follow the customer view's own rule. Long enough on screen to be read in full.
        return platformAdminApi
          .getOrgKnowledgeView(o.id)
          .then((view) => {
            const t = knowledgeShownToast(o.name, view.customer_sees);
            toast.push(t.tone, t.text, { durationMs: 15000 });
          })
          .catch(() => toast.push("ok", `Knowledge shown to ${o.name}: its members see its confirmed findings.`));
      })
      .catch(() => toast.push("error", "Couldn't switch the knowledge view."))
      .finally(() => setRowBusy(o.id + ":knowledge", false));
  };

  // A reader's own revision (org_settings.auto_revise_readers, migration 0260; R2 of
  // docs/plans/2026-10-19_reader-layout-change.md): a reader that matched one of the org's files and then failed on
  // it opens a revision of itself, which a platform admin approves. Default OFF, freely reversible.
  const toggleOrgAutoRevise = (o: OrgSummary) => {
    const next = !o.auto_revise_readers;
    setRowBusy(o.id + ":readers", true);
    platformAdminApi
      .setOrgAutoReviseReaders(o.id, next)
      .then(() => {
        setOrgs((prev) => prev.map((x) => (x.id === o.id ? { ...x, auto_revise_readers: next } : x)));
        toast.push(
          "ok",
          next
            ? `${o.name}'s readers now revise themselves when a file they matched fails; you approve each one.`
            : `${o.name}'s readers no longer revise themselves.`,
        );
      })
      .catch(() => toast.push("error", "Couldn't switch the readers' own revision."))
      .finally(() => setRowBusy(o.id + ":readers", false));
  };

  // The platform's gate for the org's improvement projects (org_settings.improvement_projects_allowed,
  // migration 0200): open, the org's admins can turn projects on (Admin, Packs); shut (the default), their
  // switch is not shown. Freely reversible; the org's projects are hidden and kept while it is shut.
  const toggleOrgProjects = (o: OrgSummary) => {
    const next = !projectsGateOpen(o);
    setRowBusy(o.id + ":projects", true);
    platformAdminApi
      .setOrgImprovementProjects(o.id, next)
      .then(() => {
        setOrgs((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, improvement_projects_allowed: next } : x,
          ),
        );
        toast.push("ok", projectsGateToast(o.name, next));
      })
      .catch(() => toast.push("error", PROJECTS_GATE_FAILED))
      .finally(() => setRowBusy(o.id + ":projects", false));
  };

  // Real/Sim reclassification (the dummy→real toggle). Flips
  // things.is_simulated across the org — mark-real also stops the gear
  // self-simulating (the data persists), mark-simulated makes the org's demo
  // readings eligible for the sim-cleanup. We reflect the returned data_class
  // on the row so its Real/Sim badge + which action shows update at once.
  const markOrgReal = (o: OrgSummary) => {
    setRowBusy(o.id + ":dataclass", true);
    platformAdminApi
      .markOrgReal(o.id)
      .then((res) => {
        setOrgs((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, data_class: res.data_class } : x,
          ),
        );
        toast.push(
          "ok",
          `Marked ${o.name} as real — ${res.things_updated} thing${res.things_updated === 1 ? "" : "s"} updated.`,
        );
      })
      .catch(() => toast.push("error", "Couldn't mark the org as real."))
      .finally(() => setRowBusy(o.id + ":dataclass", false));
  };

  // Mark-simulated is guarded server-side: a 409 ("org_has_real_data") means
  // the org holds imported/vendor equipment — a real customer — so we surface
  // that inline (same error-code read as the genealogy switch) rather than
  // crashing.
  const markOrgSimulated = (o: OrgSummary) => {
    setRowBusy(o.id + ":dataclass", true);
    platformAdminApi
      .markOrgSimulated(o.id)
      .then((res) => {
        setOrgs((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, data_class: res.data_class } : x,
          ),
        );
        toast.push(
          "ok",
          `Marked ${o.name} as simulated — ${res.things_updated} thing${res.things_updated === 1 ? "" : "s"} updated.`,
        );
      })
      .catch((err: unknown) => {
        const code =
          typeof err === "object" && err && "body" in err
            ? (err as { body?: { error?: string } }).body?.error
            : undefined;
        toast.push(
          "error",
          code === "org_has_real_data"
            ? "This org holds real imported data — it can't be marked simulated."
            : "Couldn't mark the org as simulated.",
        );
      })
      .finally(() => setRowBusy(o.id + ":dataclass", false));
  };

  const toggleUserDisabled = (u: UserSummary) => {
    const next = !u.is_disabled;
    setConfirmUser(null);
    setRowBusy(u.id, true);
    platformAdminApi
      .setUserDisabled(u.id, next)
      .then(() => {
        setUsers((prev) =>
          prev.map((x) => (x.id === u.id ? { ...x, is_disabled: next } : x)),
        );
        toast.push(
          "ok",
          next
            ? `${u.display_name || u.email} disabled`
            : `${u.display_name || u.email} enabled`,
        );
      })
      .catch((err: unknown) => {
        const code =
          typeof err === "object" && err && "body" in err
            ? (err as { body?: { error?: string } }).body?.error
            : undefined;
        toast.push(
          "error",
          code === "cannot_disable_self"
            ? "You can't disable your own account."
            : "Couldn't update the user.",
        );
      })
      .finally(() => setRowBusy(u.id, false));
  };

  // Access requests (sign-up approval gate). Approve/reject flip the user's
  // approval_status server-side; either way the request leaves the queue, so we
  // drop the row optimistically. We also reflect the new status on the user in
  // the Users table so the two views stay consistent without a refetch.
  const approveRequest = (u: UserSummary) => {
    setRowBusy(u.id + ":approval", true);
    platformAdminApi
      .approveUser(u.id)
      .then(() => {
        setUsers((prev) =>
          prev.map((x) =>
            x.id === u.id ? { ...x, approval_status: "approved" } : x,
          ),
        );
        toast.push("ok", `Approved ${u.display_name || u.email}`);
      })
      .catch(() => toast.push("error", "Couldn't approve the request."))
      .finally(() => setRowBusy(u.id + ":approval", false));
  };

  const rejectRequest = (u: UserSummary) => {
    setConfirmReject(null);
    setRowBusy(u.id + ":approval", true);
    platformAdminApi
      .rejectUser(u.id)
      .then(() => {
        setUsers((prev) =>
          prev.map((x) =>
            x.id === u.id ? { ...x, approval_status: "rejected" } : x,
          ),
        );
        toast.push("ok", `Rejected ${u.display_name || u.email}`);
      })
      .catch(() => toast.push("error", "Couldn't reject the request."))
      .finally(() => setRowBusy(u.id + ":approval", false));
  };

  // Super-admin hard-delete: DELETE /v1/platform/orgs/{id} (cascades all the
  // org's data). Drops the row from the list on success. Gated by the
  // typed-confirmation dialog (must type the org name).
  const deleteOrg = async (o: OrgSummary) => {
    setRowBusy(o.id, true);
    try {
      await platformAdminApi.deleteOrg(o.id);
      setOrgs((prev) => prev.filter((x) => x.id !== o.id));
      toast.push("ok", `Deleted ${o.name}.`);
    } catch (err) {
      toast.push("error", backendErrorMessage(err, "the organisation"));
    } finally {
      setRowBusy(o.id, false);
      setDeleteOrgTarget(null);
    }
  };

  // Bulk-purge an org's demo data: POST /v1/admin/factory/orgs/{id}/purge-simulated.
  //
  // This is the step that makes an onboarding honest. A plant built from a template
  // arrives with every tag streaming simulated values; import a customer's real history
  // on top and the entity port now resolves TWO tags — portvalues MEANS them, so every
  // number on screen is half-invented. Worse, the sim readings are stamped NOW, so they
  // beat imported history as "latest value" and the gauges show a fabricated number
  // while claiming to be the customer's plant.
  //
  // The endpoint has existed since the factory panel shipped; it just had no button, so
  // "clear the demo data" meant purging gateways one at a time (47 of them on a standard
  // green-sand plant) — a ritual nobody would complete correctly under demo pressure.
  //
  // Guarded server-side to is_simulated rows and scoped by org, so tenant-authored Things
  // survive. Bindings to purged tags cascade away and widgets fall to their empty state:
  // that emptiness is the intended, honest result.
  const purgeOrgSimulated = async (o: OrgSummary) => {
    setRowBusy(o.id, true);
    try {
      const res = await factoryApi.purgeOrgSimulated(o.id);
      toast.push(
        "ok",
        res.purged > 0
          ? `Purged ${res.purged} simulated device${res.purged === 1 ? "" : "s"} from ${o.name}. Real data untouched.`
          : `${o.name} had no simulated devices — nothing to purge.`,
      );
      // Re-fetch rather than patch the row locally: the purge response reports what it
      // deleted, not the org's resulting data_class — and that class is exactly what
      // decides whether this button should still be offered. Guessing it here is how a
      // row ends up claiming "mixed" with nothing simulated left in it.
      const fresh = await platformAdminApi.orgs();
      setOrgs(fresh.items);
    } catch (err) {
      toast.push("error", backendErrorMessage(err, "the simulated data"));
    } finally {
      setRowBusy(o.id, false);
      setPurgeSimTarget(null);
    }
  };

  // Wait for /auth/me before deciding — else we'd redirect a platform admin
  // during the sub-second identity fetch.
  if (!identityLoaded) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 py-10 text-sm text-on-surface-muted">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      </PageContainer>
    );
  }
  if (!isPlatformAdmin) {
    return <Navigate to="/app" replace />;
  }

  const tabDefs: {
    id: PlatformTab;
    label: string;
    icon: LucideIcon;
    count?: number;
    alert?: number;
  }[] = [
    { id: "orgs", label: "Organisations", icon: Building2, count: orgs.length },
    {
      id: "users",
      label: "Users",
      icon: Users,
      count: users.length,
      alert: pending.length,
    },
    { id: "gateways", label: "Gateways", icon: Router },
    { id: "factory", label: "Mock-device factory", icon: Boxes },
    { id: "firmware", label: "Firmware / OTA", icon: Rocket },
    { id: "knowledge", label: "Knowledge candidates", icon: Lightbulb },
    { id: "acquisition", label: "Acquisition learning", icon: GraduationCap },
  ];

  return (
    <PageContainer>
      <PageHeader
        breadcrumb={
          <>
            <Link to="/app" className="hover:text-on-surface">
              App
            </Link>
            <span aria-hidden>/</span>
            <span className="text-on-surface">Platform console</span>
          </>
        }
        title="Platform console"
        description="Cross-tenant god-view — every organisation and user on this install."
        actions={
          <Badge variant="info">
            <Shield className="mr-1 size-3" />
            Super-admin
          </Badge>
        }
      />

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-on-surface-muted">
          <Loader2 className="size-4 animate-spin" /> Loading platform data…
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Section tabs — this is a dense internal god-view; tabs keep it to
              one screen instead of a long scroll through five stacked tables. */}
          <div
            role="tablist"
            className="flex flex-wrap items-center gap-1 rounded-lg border border-border bg-surface-muted/40 p-1"
          >
            {tabDefs.map((t) => {
              const TabIcon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.id)}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    active
                      ? "bg-surface-elevated text-on-surface shadow-sm ring-1 ring-inset ring-border"
                      : "text-on-surface-muted hover:bg-surface-muted hover:text-on-surface",
                  )}
                >
                  <TabIcon className="size-4" />
                  {t.label}
                  {typeof t.count === "number" && (
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                        active
                          ? "bg-brand-muted text-brand"
                          : "bg-surface-muted text-on-surface-muted",
                      )}
                    >
                      {t.count}
                    </span>
                  )}
                  {typeof t.alert === "number" && t.alert > 0 && (
                    <span
                      className="rounded-full bg-warning/20 px-1.5 py-0.5 text-[10px] font-semibold text-warning"
                      title={`${t.alert} access request${t.alert === 1 ? "" : "s"} awaiting review`}
                    >
                      {t.alert}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Organisations ──────────────────────────────────────────── */}
          {activeTab === "orgs" && (
            <Card>
              <CardHeader className="flex flex-row items-center gap-3">
                <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
                  <Building2 className="size-5" />
                </div>
                <CardTitle className="flex-1">
                  Organisations
                  <span className="ml-2 text-sm font-normal text-on-surface-muted">
                    {filteredOrgs.length === orgs.length
                      ? orgs.length
                      : `${filteredOrgs.length} / ${orgs.length}`}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search (name/slug) + real/sim filter (#579) */}
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <div className="relative min-w-[12rem] flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-on-surface-muted" />
                    <Input
                      value={orgSearch}
                      onChange={(e) => {
                        setOrgSearch(e.target.value);
                        setOrgPage(1);
                      }}
                      placeholder="Search name or slug…"
                      className="pl-8"
                    />
                  </div>
                  {(["all", "real", "simulated"] as const).map((f) => (
                    <Button
                      key={f}
                      variant={orgDataClass === f ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => {
                        setOrgDataClass(f);
                        setOrgPage(1);
                      }}
                    >
                      {f === "all" ? "All" : f === "real" ? "Real" : "Sim"}
                    </Button>
                  ))}
                </div>

                {/* Real columnar table — the toggles live in labelled columns
                    (Twin/Physics/Simulator/Plan view/Knowledge/Projects/Consultant/Model) and the rare + destructive
                    actions collapse into a per-row ⋯ menu, so the org name gets
                    the width it needs instead of being crushed by a button wall.
                    overflow-x-auto keeps a narrow viewport from breaking the page
                    (desktop shows every column without scrolling). */}
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full min-w-[1170px] border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-border bg-surface-muted/40 text-left text-[11px] font-medium uppercase tracking-wide text-on-surface-muted">
                        <th className="px-3 py-2 font-medium">Organisation</th>
                        <th className="px-3 py-2 font-medium">Provenance</th>
                        <th className="px-3 py-2 text-center font-medium">Twin</th>
                        <th className="px-3 py-2 text-center font-medium">Physics</th>
                        <th className="px-3 py-2 text-center font-medium">Simulator</th>
                        <th className="px-3 py-2 text-center font-medium">Plan view</th>
                        <th className="px-3 py-2 text-center font-medium">Knowledge</th>
                        <th className="px-3 py-2 text-center font-medium">Readers</th>
                        <th
                          className="px-3 py-2 text-center font-medium"
                          title={PROJECTS_GATE_HEADER_TITLE}
                        >
                          Projects
                        </th>
                        <th className="px-3 py-2 text-center font-medium">Consultant</th>
                        <th className="px-3 py-2 text-center font-medium">Products</th>
                        <th className="px-3 py-2 text-center font-medium">Model</th>
                        <th className="px-3 py-2 text-center font-medium">Status</th>
                        <th className="px-3 py-2 text-right font-medium">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedOrgs.length === 0 && (
                        <tr>
                          <td
                            colSpan={13}
                            className="px-3 py-6 text-center text-on-surface-muted"
                          >
                            No organisations match your search or filter.
                          </td>
                        </tr>
                      )}
                      {pagedOrgs.map((o) => {
                        const dcb = dataClassBadge(o.data_class);
                        const simOrMixed =
                          o.data_class === "simulated" ||
                          o.data_class === "mixed";
                        return (
                          <tr
                            key={o.id}
                            className={cn(
                              "border-b border-border last:border-b-0",
                              o.is_disabled && "bg-danger/5",
                            )}
                          >
                            {/* Identity — now with room to actually read the
                                name + slug/members/created meta line. */}
                            <td className="px-3 py-2.5">
                              <div className="flex min-w-0 flex-col">
                                <span
                                  className="block max-w-[24rem] truncate font-medium text-on-surface"
                                  title={o.name}
                                >
                                  {o.name}
                                </span>
                                <span className="block max-w-[24rem] truncate font-mono text-[11px] text-on-surface-muted">
                                  {o.slug} · {o.member_count} member
                                  {o.member_count === 1 ? "" : "s"} ·{" "}
                                  {fmtDate(o.created_at)}
                                </span>
                              </div>
                            </td>

                            {/* Provenance (data_class) */}
                            <td className="px-3 py-2.5">
                              <Badge
                                variant={dcb.variant}
                                title="Equipment provenance — real (imported/vendor) vs simulated (demo)"
                              >
                                {dcb.label}
                              </Badge>
                            </td>

                            {/* Digital-twin (foundry pack) toggle */}
                            <td className="px-3 py-2.5 text-center">
                              <OrgToggle
                                on={o.twin_enabled}
                                busy={busy.has(o.id + ":twin")}
                                onClick={() => toggleOrgTwin(o)}
                                icon={Network}
                                onTitle="Digital twin ON for this org — click to disable the Tier-2 twin surface"
                                offTitle="Digital twin OFF — click to enable the Tier-2 twin surface"
                              />
                            </td>

                            {/* Physics-twin opt-in
                                (org_settings.physics_twin_enabled) — gates the S2
                                live physics tick, the reconcile worker and
                                /v1/twin/simcore/*. Default OFF; the org's pack
                                behaviour graphs are inert until this is on. */}
                            <td className="px-3 py-2.5 text-center">
                              <OrgToggle
                                on={o.physics_twin_enabled}
                                busy={busy.has(o.id + ":physics")}
                                onClick={() => toggleOrgPhysicsTwin(o)}
                                icon={Atom}
                                onTitle="Physics twin ON — behaviour-graph equipment simulates live; click to disable"
                                offTitle="Physics twin OFF — click to enable live behaviour-graph simulation + reconciliation"
                              />
                            </td>

                            {/* Per-org demo-simulator kill switch
                                (org_settings.simulator_enabled, migration 0092,
                                #581). Opt-OUT: default ON. Freezing halts the
                                simulator tick's new readings for THIS org only —
                                the per-org alternative to the global
                                SIMULATOR_DISABLED env flag (e.g. freeze just
                                before importing a customer's real data). */}
                            <td className="px-3 py-2.5 text-center">
                              <OrgToggle
                                on={o.simulator_enabled}
                                busy={busy.has(o.id + ":sim")}
                                onClick={() => toggleOrgSimulator(o)}
                                icon={Activity}
                                onTitle="Demo simulator running — click to freeze (stops new simulated readings for this org only)"
                                offTitle="Demo simulator frozen — click to resume generating demo readings"
                              />
                            </td>

                            {/* Per-org analysis plan view
                                (org_settings.analysis_plan_view, migration 0169,
                                plant-analytics S14): the Plan tab of the org's
                                correlations page. Default OFF. */}
                            <td className="px-3 py-2.5 text-center">
                              <OrgToggle
                                on={o.analysis_plan_view}
                                busy={busy.has(o.id + ":planview")}
                                onClick={() => toggleOrgPlanView(o)}
                                icon={ListChecks}
                                onTitle="Plan view shown: the Plan tab is on this org's correlations page. Click to hide it"
                                offTitle="Plan view hidden. Click to show the Plan tab on this org's correlations page"
                              />
                            </td>

                            {/* The knowledge store's customer switch
                                (org_settings.knowledge_customer_visible, migration
                                0156, plant-analytics B-6b): the org's members see
                                its confirmed findings. Default OFF. */}
                            <td className="px-3 py-2.5 text-center">
                              <OrgToggle
                                on={o.knowledge_customer_visible}
                                busy={busy.has(o.id + ":knowledge")}
                                onClick={() => toggleOrgKnowledge(o)}
                                icon={Brain}
                                onTitle="Knowledge shown: this org's members see its confirmed findings. Click to hide it"
                                offTitle="Knowledge hidden: this org's members see Coming soon. Click to show them its confirmed findings"
                              />
                            </td>

                            {/* A reader's own revision (org_settings.auto_revise_readers,
                                migration 0260): a matched reader that fails on a file
                                revises itself, and a platform admin approves. Default OFF. */}
                            <td className="px-3 py-2.5 text-center">
                              <OrgToggle
                                on={o.auto_revise_readers ?? false}
                                busy={busy.has(o.id + ":readers")}
                                onClick={() => toggleOrgAutoRevise(o)}
                                icon={FileCog}
                                onTitle="Readers revise themselves: a reader that fails on a file it matched opens its own update, which you approve. Click to stop it"
                                offTitle="Readers do not revise themselves: a file that fails its reader waits for a person. Click to let them"
                              />
                            </td>

                            {/* The platform's gate for improvement projects
                                (org_settings.improvement_projects_allowed, migration
                                0200): open, the org's admins can turn them on.
                                Default shut. */}
                            <td className="px-3 py-2.5 text-center">
                              <OrgToggle
                                on={projectsGateOpen(o)}
                                busy={busy.has(o.id + ":projects")}
                                onClick={() => toggleOrgProjects(o)}
                                icon={FolderKanban}
                                onTitle={PROJECTS_GATE_ON_TITLE}
                                offTitle={PROJECTS_GATE_OFF_TITLE}
                              />
                            </td>

                            {/* The AI Casting Consultant's daily limits for
                                the org (migration 0201): opens the sheet with
                                its limits and their sources, today's usage and
                                the resets. */}
                            <td className="px-3 py-2.5 text-center">
                              <ConsultantLimitsCell org={o} onOpen={() => setLimitsOrg(o)} />
                            </td>

                            {/* The suite products the org holds, and its model
                                training opt-in (migration 0227): opens the sheet. */}
                            <td className="px-3 py-2.5 text-center">
                              <ProductsCell org={o} onOpen={() => setProductsOrg(o)} />
                            </td>

                            {/* Genealogy-model switch (legacy ↔ new) — flips the
                                org's plant.source, the signal the new emitter +
                                useNewModel gate read. The pill shows the current
                                model; clicking switches it. */}
                            <td className="px-3 py-2.5 text-center">
                              <button
                                type="button"
                                disabled={busy.has(o.id + ":model")}
                                onClick={() => toggleOrgGenealogyModel(o)}
                                title={
                                  o.genealogy_model === "new"
                                    ? "New genealogy model — click to switch this org back to legacy"
                                    : "Legacy genealogy model — click to switch to the new model (regenerate its heats after)"
                                }
                                className={cn(
                                  "inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                  o.genealogy_model === "new"
                                    ? "bg-info/10 text-info ring-info/30 hover:bg-info/20"
                                    : "bg-surface-muted text-on-surface-muted ring-border hover:bg-surface-muted/70 hover:text-on-surface",
                                )}
                              >
                                <GitBranch className="size-3" />
                                {o.genealogy_model === "new"
                                  ? "new · beta"
                                  : "legacy"}
                              </button>
                            </td>

                            {/* Status */}
                            <td className="px-3 py-2.5 text-center">
                              {o.is_disabled ? (
                                <Badge variant="danger">Disabled</Badge>
                              ) : (
                                <span className="text-xs text-on-surface-muted">
                                  Active
                                </span>
                              )}
                            </td>

                            {/* Actions — reclassify, purge, disable/enable and
                                delete collapse into a ⋯ menu. Disable keeps its
                                two-click inline confirm, rendered here in place
                                of the menu once armed. */}
                            <td className="px-3 py-2.5">
                              {confirmOrg === o.id ? (
                                <div className="flex items-center justify-end gap-1.5">
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    disabled={busy.has(o.id)}
                                    onClick={() => toggleOrgDisabled(o)}
                                  >
                                    Confirm disable
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setConfirmOrg(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex justify-end">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="size-8"
                                        title="More actions"
                                        disabled={busy.has(o.id)}
                                      >
                                        <MoreHorizontal className="size-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="w-56"
                                    >
                                      {/* Real/Sim reclassification (the dummy→real
                                          toggle) — flips things.is_simulated across
                                          the org. "Mark as real" shows for
                                          simulated/mixed orgs (stops the sim; data
                                          persists); "Mark as simulated" for
                                          real/empty orgs, and is refused server-side
                                          (409 org_has_real_data) on orgs that hold
                                          real imported data. */}
                                      {simOrMixed ? (
                                        <DropdownMenuItem
                                          disabled={busy.has(o.id + ":dataclass")}
                                          onSelect={() => markOrgReal(o)}
                                        >
                                          <Factory className="text-success" />
                                          Mark as real
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem
                                          disabled={busy.has(o.id + ":dataclass")}
                                          onSelect={() => markOrgSimulated(o)}
                                        >
                                          <FlaskConical className="text-warning" />
                                          Mark as simulated
                                        </DropdownMenuItem>
                                      )}

                                      {/* Purge demo data. Offered only where there
                                          is any: an org marked `real` with no
                                          simulated Things has nothing to clear, and
                                          a destructive no-op just invites a
                                          mis-click. */}
                                      {simOrMixed && (
                                        <DropdownMenuItem
                                          disabled={busy.has(o.id)}
                                          onSelect={() => setPurgeSimTarget(o)}
                                        >
                                          <Eraser className="text-warning" />
                                          Purge demo data…
                                        </DropdownMenuItem>
                                      )}

                                      {/* Copy another org's setup into this one
                                          (plant model, plant time, data sources,
                                          extractors, windows, words, products):
                                          the sheet says what is and is not copied
                                          and asks for this org's name first. */}
                                      <DropdownMenuItem
                                        disabled={busy.has(o.id)}
                                        onSelect={() => setCopySetupOrg(o)}
                                      >
                                        <Copy />
                                        Copy setup from another org…
                                      </DropdownMenuItem>

                                      <DropdownMenuSeparator />

                                      {/* Disable / enable */}
                                      {o.is_disabled ? (
                                        <DropdownMenuItem
                                          disabled={busy.has(o.id)}
                                          onSelect={() => toggleOrgDisabled(o)}
                                        >
                                          <CircleCheck className="text-success" />
                                          Enable organisation
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem
                                          disabled={busy.has(o.id)}
                                          onSelect={() => setConfirmOrg(o.id)}
                                        >
                                          <Ban />
                                          Disable organisation
                                        </DropdownMenuItem>
                                      )}

                                      {/* Hard delete (typed confirmation) —
                                          cascades all the org's data. */}
                                      <DropdownMenuItem
                                        disabled={busy.has(o.id)}
                                        onSelect={() => setDeleteOrgTarget(o)}
                                        className="text-danger focus:bg-danger/10"
                                      >
                                        <Trash2 />
                                        Delete organisation…
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {filteredOrgs.length > PAGE_SIZE && (
                  <div className="mt-3 flex items-center justify-between text-xs text-on-surface-muted">
                    <span>
                      Showing {(effOrgPage - 1) * PAGE_SIZE + 1}–
                      {Math.min(effOrgPage * PAGE_SIZE, filteredOrgs.length)} of{" "}
                      {filteredOrgs.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={effOrgPage <= 1}
                        onClick={() => setOrgPage(effOrgPage - 1)}
                      >
                        Prev
                      </Button>
                      <span>
                        Page {effOrgPage} / {orgPageCount}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={effOrgPage >= orgPageCount}
                        onClick={() => setOrgPage(effOrgPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Users (+ access-request queue) ─────────────────────────── */}
          {activeTab === "users" && (
            <>
              {/* Access requests (sign-up approval gate) — the actionable queue,
                  rendered above the full Users table. */}
              {pending.length > 0 && (
                <Card>
                  <CardHeader className="flex flex-row items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
                      <UserCheck className="size-5" />
                    </div>
                    <CardTitle className="flex-1">
                      Access requests
                      <span className="ml-2 text-sm font-normal text-on-surface-muted">
                        {pending.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-hidden rounded-md border border-border">
                      {pending.map((u, idx) => (
                        <div
                          key={u.id}
                          className={cn(
                            "flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm",
                            idx > 0 && "border-t border-border",
                          )}
                        >
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate font-medium text-on-surface">
                              {u.applicant_name || u.display_name || u.email}
                            </span>
                            <span className="truncate text-[11px] text-on-surface-muted">
                              {u.email}
                              {u.applicant_company
                                ? ` · ${u.applicant_company}`
                                : ""}
                              {u.applicant_position
                                ? ` · ${u.applicant_position}`
                                : ""}
                              {u.applicant_phone
                                ? ` · ${u.applicant_phone}`
                                : ""}
                              {" · "}
                              {fmtDate(u.created_at)}
                            </span>
                          </div>

                          {confirmReject === u.id ? (
                            <div className="flex items-center gap-1.5">
                              <Button
                                variant="danger"
                                size="sm"
                                disabled={busy.has(u.id + ":approval")}
                                onClick={() => rejectRequest(u)}
                              >
                                Confirm reject
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmReject(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={busy.has(u.id + ":approval")}
                                onClick={() => approveRequest(u)}
                              >
                                <Check className="size-3.5" />
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={busy.has(u.id + ":approval")}
                                onClick={() => setConfirmReject(u.id)}
                                className="text-danger hover:bg-danger/10"
                              >
                                <X className="size-3.5" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Users */}
              <Card>
                <CardHeader className="flex flex-row items-center gap-3">
                  <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
                    <Users className="size-5" />
                  </div>
                  <CardTitle className="flex-1">
                    Users
                    <span className="ml-2 text-sm font-normal text-on-surface-muted">
                      {filteredUsers.length === users.length
                        ? users.length
                        : `${filteredUsers.length} / ${users.length}`}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Search (name/email) (#579) */}
                  <div className="relative mb-3 min-w-[12rem]">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-on-surface-muted" />
                    <Input
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setUserPage(1);
                      }}
                      placeholder="Search name or email…"
                      className="pl-8"
                    />
                  </div>
                  <div className="overflow-hidden rounded-md border border-border">
                    {pagedUsers.length === 0 && (
                      <div className="px-3 py-6 text-center text-sm text-on-surface-muted">
                        No users match your search.
                      </div>
                    )}
                    {pagedUsers.map((u, idx) => {
                      const expanded = expandedUser === u.id;
                      const memberships = userOrgs[u.id];
                      return (
                        <div
                          key={u.id}
                          className={cn(idx > 0 && "border-t border-border")}
                        >
                          <div
                            className={cn(
                              "flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm",
                              u.is_disabled && "bg-danger/5",
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => toggleUserExpand(u.id)}
                              className="flex size-5 shrink-0 items-center justify-center rounded text-on-surface-muted hover:bg-surface-muted hover:text-on-surface"
                              title={
                                expanded ? "Collapse" : "Show org memberships"
                              }
                              aria-expanded={expanded}
                            >
                              <ChevronRight
                                className={cn(
                                  "size-4 transition-transform",
                                  expanded && "rotate-90",
                                )}
                              />
                            </button>
                            <div className="flex min-w-0 flex-1 flex-col">
                              <span className="flex items-center gap-1.5 truncate font-medium text-on-surface">
                                {u.display_name || u.email}
                                {u.is_platform_admin && (
                                  <Badge
                                    variant="default"
                                    className="text-[9px]"
                                  >
                                    <Shield className="mr-0.5 size-2.5" />
                                    admin
                                  </Badge>
                                )}
                                {u.is_disabled && (
                                  <Badge variant="danger" className="text-[9px]">
                                    disabled
                                  </Badge>
                                )}
                              </span>
                              <span className="truncate text-[11px] text-on-surface-muted">
                                {u.email} · {u.org_count} org
                                {u.org_count === 1 ? "" : "s"} ·{" "}
                                {fmtDate(u.created_at)}
                              </span>
                            </div>

                            {/* Platform admins are not disable-able from the
                                console (protects the operator's own account; the
                                backend also guards self-disable). */}
                            {u.is_platform_admin ? (
                              <span className="text-[11px] italic text-on-surface-muted">
                                protected
                              </span>
                            ) : confirmUser === u.id ? (
                              <div className="flex items-center gap-1.5">
                                <Button
                                  variant="danger"
                                  size="sm"
                                  disabled={busy.has(u.id)}
                                  onClick={() => toggleUserDisabled(u)}
                                >
                                  Confirm disable
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setConfirmUser(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : u.is_disabled ? (
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={busy.has(u.id)}
                                onClick={() => toggleUserDisabled(u)}
                              >
                                <CircleCheck className="size-3.5" />
                                Enable
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={busy.has(u.id)}
                                onClick={() => setConfirmUser(u.id)}
                              >
                                <Ban className="size-3.5" />
                                Disable
                              </Button>
                            )}
                          </div>
                          {expanded && (
                            <div className="border-t border-border bg-surface-muted/40 px-3 py-2.5 pl-10">
                              {busy.has(u.id + ":orgs") ? (
                                <span className="flex items-center gap-2 text-xs text-on-surface-muted">
                                  <Loader2 className="size-3.5 animate-spin" />
                                  Loading memberships…
                                </span>
                              ) : userOrgsErr[u.id] ? (
                                <span className="text-xs text-danger">
                                  Couldn't load this user's memberships.
                                </span>
                              ) : memberships && memberships.length > 0 ? (
                                <div className="flex flex-col gap-1.5">
                                  <span className="text-[11px] uppercase tracking-wide text-on-surface-muted">
                                    Member of {memberships.length} org
                                    {memberships.length === 1 ? "" : "s"}
                                  </span>
                                  {memberships.map((m) => (
                                    <div
                                      key={m.org_id}
                                      className="flex items-center gap-2 text-xs"
                                    >
                                      <span className="font-medium text-on-surface">
                                        {m.name}
                                      </span>
                                      <span className="text-on-surface-muted">
                                        {m.slug}
                                      </span>
                                      <Badge
                                        variant={
                                          m.is_owner ? "default" : "secondary"
                                        }
                                        className="text-[9px]"
                                      >
                                        {m.is_owner ? "owner" : m.role}
                                      </Badge>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-on-surface-muted">
                                  Not a member of any org.
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {filteredUsers.length > PAGE_SIZE && (
                    <div className="mt-3 flex items-center justify-between text-xs text-on-surface-muted">
                      <span>
                        Showing {(effUserPage - 1) * PAGE_SIZE + 1}–
                        {Math.min(effUserPage * PAGE_SIZE, filteredUsers.length)}{" "}
                        of {filteredUsers.length}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={effUserPage <= 1}
                          onClick={() => setUserPage(effUserPage - 1)}
                        >
                          Prev
                        </Button>
                        <span>
                          Page {effUserPage} / {userPageCount}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={effUserPage >= userPageCount}
                          onClick={() => setUserPage(effUserPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* ── Gateways: the list across orgs, one gateway's details and its remote diagnostics (remote console P0) ── */}
          {activeTab === "gateways" && (
            <GatewaysConsole orgs={orgs.map((o) => ({ id: o.id, name: o.name, slug: o.slug }))} />
          )}

          {/* ── Mock-device factory (Phase 2) ──────────────────────────── */}
          {activeTab === "factory" && <FactoryPanel />}

          {/* ── Firmware / OTA control plane ───────────────────────────── */}
          {activeTab === "firmware" && <FirmwareConsole />}

          {/* ── Knowledge candidates: the shared foundry knowledge's curation (L3) ── */}
          {activeTab === "knowledge" && <KnowledgeCandidatesPanel />}

          {/* ── Acquisition learning: parameter corrections made alike at several organisations, for the pack (P7) ── */}
          {activeTab === "acquisition" && <AcquisitionLearningPanel />}
        </div>
      )}

      <DangerConfirmDialog
        open={deleteOrgTarget !== null}
        onClose={() => setDeleteOrgTarget(null)}
        onConfirm={() => {
          if (deleteOrgTarget) return deleteOrg(deleteOrgTarget);
        }}
        title="Delete this organisation?"
        description={
          deleteOrgTarget ? (
            <>
              This permanently deletes{" "}
              <span className="font-medium text-on-surface">{deleteOrgTarget.name}</span>{" "}
              and all of its data ({deleteOrgTarget.member_count} member
              {deleteOrgTarget.member_count === 1 ? "" : "s"}). This cannot be
              undone.
            </>
          ) : null
        }
        confirmPhrase={deleteOrgTarget?.name ?? ""}
        confirmLabel="Delete organisation"
      />

      {/* Typed-confirm, same as delete: this drops readings irreversibly. The reason to
          reach for it is honesty — a template plant streams simulated values on every
          tag, and importing a customer's real history on top leaves BOTH bound to the
          same port, which portvalues averages. */}
      <DangerConfirmDialog
        open={purgeSimTarget !== null}
        onClose={() => setPurgeSimTarget(null)}
        onConfirm={() => {
          if (purgeSimTarget) return purgeOrgSimulated(purgeSimTarget);
        }}
        title="Purge this org's demo data?"
        description={
          purgeSimTarget ? (
            <>
              This deletes every simulated device in{" "}
              <span className="font-medium text-on-surface">{purgeSimTarget.name}</span>{" "}
              and the readings they produced. Real imported or vendor data is untouched,
              and the plant model itself stays — equipment simply stops showing demo
              values until real data arrives. This cannot be undone.
            </>
          ) : null
        }
        confirmPhrase={purgeSimTarget?.name ?? ""}
        confirmLabel="Purge demo data"
      />

      {/* The org's suite products and training opt-in (migration 0227), keyed by org so each opens afresh. */}
      {productsOrg && (
        <ProductsSheet key={productsOrg.id} org={productsOrg} onClose={() => setProductsOrg(null)} />
      )}
      {/* The consultant's limits (migration 0201), keyed by org so each opens afresh. */}
      {limitsOrg && (
        <ConsultantLimitsSheet key={limitsOrg.id} org={limitsOrg} onClose={() => setLimitsOrg(null)} />
      )}

      {/* Copy setup from another org, keyed by org so each opens afresh. Once a copy is made
          the orgs are read again: a copy can change what an org's data class says. */}
      {copySetupOrg && (
        <CopySetupSheet
          key={copySetupOrg.id}
          target={copySetupOrg}
          orgs={orgs}
          onClose={() => setCopySetupOrg(null)}
          onCopied={() => {
            void platformAdminApi
              .orgs()
              .then((fresh) => setOrgs(fresh.items))
              .catch(() => {});
          }}
        />
      )}

    </PageContainer>
  );
}
